/**
 * Admin → Users tab.
 *
 * List of every user with site counts and the per-user cap override. Edit
 * panel shows the user's full info, every site they own (deep-link to site
 * admin edit), plus controls to change is_admin, max_sites_override and the
 * default build profile stamped on the user's new sites.
 */
import { useEffect, useRef, useState } from 'hono/jsx';
import { AdminShell } from '../../components/AdminShell';
import { BUILD_ENGINES } from './Sites';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { navigate } from '../../lib/router';
import { Spinner } from '../../components/Spinner';

interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
  isAdmin: boolean;
  maxSitesOverride: number | null;
  defaultProfile: string | null;
  siteCount: number;
  createdAt: string;
  updatedAt: string;
}

interface UserSite {
  id: string;
  slug: string;
  name: string;
  status: string;
  domain: string | null;
  publishedAt: string | null;
  createdAt: string;
}

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const searchTimer = useRef<number | null>(null);

  async function load(q = search) {
    setLoading(true);
    setLoadError(null);
    let url = '/admin/users?limit=50';
    if (q) url += '&search=' + encodeURIComponent(q);
    try {
      const r = await api(url);
      if (!r.ok) throw new Error('Failed to load users');
      const data = await r.json() as { users?: AdminUser[] };
      setUsers(data.users || []);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(''); }, []);

  function onSearchInput(e: Event) {
    const v = (e.target as HTMLInputElement).value;
    setSearch(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(v), 300) as unknown as number;
  }

  return (
    <AdminShell active="users">
      <div class="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-slate-100">All Users</h2>
        <input
          type="text"
          value={search}
          onInput={onSearchInput}
          placeholder="Search by email or name..."
          class="px-3 py-1.5 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 w-72"
        />
      </div>

      {editing && (
        <EditUserPanel
          user={editing}
          onClose={() => setEditing(null)}
          onReload={() => load(search)}
        />
      )}

      <div class="bg-dark-800 rounded-xl border border-slate-700/50 overflow-x-auto">
        {loading ? (
          <div class="p-8 text-center"><Spinner className="mx-auto" /><p class="text-slate-400 mt-4 text-sm">Loading users...</p></div>
        ) : loadError ? (
          <div class="p-8 text-center text-red-400">{loadError}</div>
        ) : !users.length ? (
          <div class="p-8 text-center text-slate-400">No users found</div>
        ) : (
          <UsersTable users={users} onEdit={(u) => setEditing(u)} />
        )}
      </div>
    </AdminShell>
  );
}

function UsersTable({ users, onEdit }: { users: AdminUser[]; onEdit: (u: AdminUser) => void }) {
  return (
    <table class="w-full">
      <thead class="bg-dark-700">
        <tr>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Sites</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Cap</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Admin</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Joined</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-slate-700/50">
        {users.map((u) => (
          <tr class="hover:bg-dark-700/50">
            <td class="px-4 py-3">
              <div class="text-sm font-medium text-slate-200">{u.name || '—'}</div>
              <div class="text-xs text-slate-500">{u.email}</div>
            </td>
            <td class="px-4 py-3 text-sm text-slate-300">{u.siteCount}</td>
            <td class="px-4 py-3 text-sm">
              {u.maxSitesOverride == null ? (
                <span class="text-slate-500">default</span>
              ) : (
                <span class="text-cyan-400 font-medium">{u.maxSitesOverride}</span>
              )}
            </td>
            <td class="px-4 py-3">
              {u.isAdmin ? (
                <span class="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400">Admin</span>
              ) : (
                <span class="text-xs text-slate-500">—</span>
              )}
            </td>
            <td class="px-4 py-3 text-xs text-slate-500">
              {new Date(u.createdAt.replace(' ', 'T') + 'Z').toLocaleDateString()}
            </td>
            <td class="px-4 py-3">
              <button
                type="button"
                onClick={() => onEdit(u)}
                class="text-cyan-400 hover:text-cyan-300 text-sm"
              >Edit</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EditUserPanel({ user, onClose, onReload }: { user: AdminUser; onClose: () => void; onReload: () => void }) {
  const [isAdmin, setIsAdmin] = useState<boolean>(user.isAdmin);
  const [overrideMode, setOverrideMode] = useState<'default' | 'custom'>(
    user.maxSitesOverride == null ? 'default' : 'custom'
  );
  const [overrideValue, setOverrideValue] = useState<string>(
    user.maxSitesOverride == null ? '1' : String(user.maxSitesOverride)
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [sites, setSites] = useState<UserSite[] | null>(null);
  const [sitesLoading, setSitesLoading] = useState<boolean>(true);
  const userPair = (user.defaultProfile || '').split('+');
  const userPairValid = userPair.length === 2 && userPair.every((id) => BUILD_ENGINES.some((e) => e.id === id));
  const [profileMode, setProfileMode] = useState<'default' | 'custom'>(userPairValid ? 'custom' : 'default');
  const [createEngine, setCreateEngine] = useState<string>(userPairValid ? userPair[0] : 'opus-5');
  const [editEngine, setEditEngine] = useState<string>(userPairValid ? userPair[1] : 'sonnet-5');
  const [platformDefault, setPlatformDefault] = useState<string>('');
  const buildSkill = createEngine + '+' + editEngine;

  // hono/jsx/dom sets <select value=...> before the <option> children exist,
  // so re-apply it after mount and on change.
  const createSelectRef = useRef<HTMLSelectElement>(null);
  const editSelectRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (createSelectRef.current && createSelectRef.current.value !== createEngine) {
      createSelectRef.current.value = createEngine;
    }
    if (editSelectRef.current && editSelectRef.current.value !== editEngine) {
      editSelectRef.current.value = editEngine;
    }
  }, [createEngine, editEngine, profileMode]);

  useEffect(() => {
    (async () => {
      setSitesLoading(true);
      try {
        const r = await api('/admin/users/' + user.id);
        if (!r.ok) throw new Error('Failed to load user detail');
        const data = await r.json() as {
          sites?: UserSite[];
          platformDefaultProfile?: string;
        };
        setSites(data.sites || []);
        setPlatformDefault(data.platformDefaultProfile || '');
      } catch (e: any) {
        showToast('error', 'Load failed', e?.message || 'Could not load sites');
        setSites([]);
      } finally {
        setSitesLoading(false);
      }
    })();
  }, [user.id]);

  async function save() {
    const payload: { isAdmin: boolean; maxSitesOverride: number | null; defaultProfile?: string | null } = {
      isAdmin,
      maxSitesOverride: overrideMode === 'default' ? null : parseInt(overrideValue, 10),
    };
    payload.defaultProfile = profileMode === 'default' ? null : buildSkill;
    if (overrideMode === 'custom' && (!Number.isInteger(payload.maxSitesOverride) || (payload.maxSitesOverride as number) < 0)) {
      showToast('error', 'Invalid', 'Site cap must be a non-negative integer');
      return;
    }
    setSaving(true);
    try {
      const r = await api('/admin/users/' + user.id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({})) as { error?: string };
      if (!r.ok) throw new Error(data.error || 'Failed to save');
      showToast('success', 'Saved', `${user.email} updated`);
      onReload();
      onClose();
    } catch (e: any) {
      showToast('error', 'Save failed', e?.message || 'Unknown error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="bg-dark-800 rounded-xl border border-slate-700/50 p-6 mb-6">
      <div class="flex justify-between items-start mb-4">
        <h3 class="text-lg font-semibold text-slate-100">Edit User</h3>
        <button type="button" onClick={onClose} class="text-slate-400 hover:text-slate-200">✕</button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="space-y-4">
          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Account</h4>
            <div class="bg-dark-700 rounded-lg p-4 space-y-2 text-sm">
              <div class="flex justify-between gap-3"><span class="text-slate-500">Name</span><span class="text-slate-200">{user.name || '—'}</span></div>
              <div class="flex justify-between gap-3"><span class="text-slate-500">Email</span><span class="text-slate-200 break-all text-right">{user.email}</span></div>
              <div class="flex justify-between gap-3"><span class="text-slate-500">ID</span><code class="text-xs text-slate-400">{user.id}</code></div>
              <div class="flex justify-between gap-3"><span class="text-slate-500">Joined</span><span class="text-slate-200">{new Date(user.createdAt.replace(' ', 'T') + 'Z').toLocaleString()}</span></div>
            </div>
          </div>

          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Site Cap</h4>
            <div class="bg-dark-700 rounded-lg p-4 space-y-3">
              <label class="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="capMode"
                  checked={overrideMode === 'default'}
                  onChange={() => setOverrideMode('default')}
                  class="mt-1"
                />
                <div>
                  <div class="text-sm text-slate-200">Use global default</div>
                  <p class="text-xs text-slate-500">Currently 1 site per non-admin user.</p>
                </div>
              </label>
              <label class="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="capMode"
                  checked={overrideMode === 'custom'}
                  onChange={() => setOverrideMode('custom')}
                  class="mt-1"
                />
                <div class="flex-1">
                  <div class="text-sm text-slate-200">Override</div>
                  <p class="text-xs text-slate-500 mb-2">Exact number of sites this user can own.</p>
                  <input
                    type="number"
                    min="0"
                    value={overrideValue}
                    onInput={(e) => setOverrideValue((e.target as HTMLInputElement).value)}
                    disabled={overrideMode !== 'custom'}
                    class="w-24 px-2 py-1 bg-dark-800 border border-slate-600/50 rounded text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:opacity-40"
                  />
                </div>
              </label>
            </div>
          </div>

          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Build Profile</h4>
            <div class="bg-dark-700 rounded-lg p-4 space-y-3">
              <label class="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="profileMode"
                  checked={profileMode === 'default'}
                  onChange={() => setProfileMode('default')}
                  class="mt-1"
                />
                <div>
                  <div class="text-sm text-slate-200">Use platform default</div>
                  <p class="text-xs text-slate-500">Currently <code class="text-slate-400">{platformDefault || '...'}</code>.</p>
                </div>
              </label>
              <label class="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="profileMode"
                  checked={profileMode === 'custom'}
                  onChange={() => setProfileMode('custom')}
                  class="mt-1"
                />
                <div class="text-sm text-slate-200">Override</div>
              </label>
              <div>
                <label class="text-xs text-slate-400 block mb-2">Initial build &amp; rebuilds</label>
                <select
                  ref={createSelectRef}
                  value={createEngine}
                  disabled={profileMode !== 'custom'}
                  onChange={(e) => setCreateEngine((e.target as HTMLSelectElement).value)}
                  class="w-full px-3 py-2.5 bg-slate-900 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 cursor-pointer disabled:opacity-40"
                >
                  {BUILD_ENGINES.map((e) => <option value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <label class="text-xs text-slate-400 block mb-2">Updates &amp; edits</label>
                <select
                  ref={editSelectRef}
                  value={editEngine}
                  disabled={profileMode !== 'custom'}
                  onChange={(e) => setEditEngine((e.target as HTMLSelectElement).value)}
                  class="w-full px-3 py-2.5 bg-slate-900 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 cursor-pointer disabled:opacity-40"
                >
                  {BUILD_ENGINES.map((e) => <option value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <p class="text-xs text-slate-500">Applied to sites this user creates from now on. Existing sites keep their profile.</p>
            </div>
          </div>

          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Role</h4>
            <label class="bg-dark-700 rounded-lg p-4 flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isAdmin}
                onChange={(e) => setIsAdmin((e.target as HTMLInputElement).checked)}
              />
              <div>
                <div class="text-sm text-slate-200">Admin</div>
                <p class="text-xs text-slate-500">Bypasses all site caps; can access /admin.</p>
              </div>
            </label>
          </div>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            class="w-full bg-cyan-500 hover:bg-cyan-400 text-dark-900 py-2.5 rounded-lg font-semibold transition-colors disabled:opacity-60"
          >{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>

        <div>
          <h4 class="text-sm font-medium text-slate-300 mb-3">Sites ({user.siteCount})</h4>
          <div class="bg-dark-700 rounded-lg p-4 max-h-[28rem] overflow-y-auto">
            {sitesLoading ? (
              <div class="text-center py-6"><Spinner className="mx-auto" /></div>
            ) : !sites?.length ? (
              <div class="text-center text-sm text-slate-500 py-6">No sites yet</div>
            ) : (
              <ul class="space-y-2">
                {sites.map((s) => (
                  <li>
                    <a
                      href={`/admin?site=${s.id}`}
                      onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/admin'); }}
                      class="block bg-dark-800 hover:bg-slate-700 border border-slate-600/30 rounded-lg p-3 transition-colors"
                    >
                      <div class="flex justify-between items-start gap-2">
                        <div>
                          <div class="text-sm font-medium text-slate-200">{s.name}</div>
                          <div class="text-xs text-slate-500">{s.slug}{s.domain ? ` · ${s.domain}` : ''}</div>
                        </div>
                        <span class={`text-xs px-2 py-0.5 rounded ${s.status === 'live' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>{s.status}</span>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
